import React from "react";
import {
  Label,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useTheme, Box, Typography, useMediaQuery } from "@mui/material";
import { formatCash } from "../../utils/helper";

interface ChartData {
  name: string;
  value: number;
  color: string;
}

export interface DonutChartProps {
  id?: string;
  data?: ChartData[];
}

interface LabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  value: number;
  payload: ChartData;
}

const DonutChart: React.FC<DonutChartProps> = ({ id, data = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";

  const total = React.useMemo(
    () => data.reduce((prev, curr) => prev + (curr?.value || 0), 0),
    [data]
  );

  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  // Calculate responsive sizes
  const innerRadius = React.useMemo(() => (isMobile ? 50 : 70), [isMobile]);
  const outerRadius = React.useMemo(() => (isMobile ? 85 : 110), [isMobile]);
  const fontSize = React.useMemo(() => (isMobile ? 20 : 30), [isMobile]);

  // Custom label component
  const renderCustomLabel = (props: LabelProps) => {
    const { cx, cy, percent } = props;
    if (percent < 0.01) return null; // Don't show label for very small slices

    return (
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={isDark ? theme.palette.text.primary : theme.palette.text.primary}
        fontSize={fontSize}
        fontWeight={600}
        style={{
          filter: isDark ? "drop-shadow(0 2px 4px rgba(0,0,0,0.5))" : "none",
        }}
      >
        {formatCash(total)}
      </text>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percent = ((data.value / total) * 100).toFixed(1);
      return (
        <Box
          sx={{
            backgroundColor: isDark
              ? "rgba(0, 0, 0, 0.9)"
              : "rgba(255, 255, 255, 0.95)",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            padding: 1.5,
            boxShadow: theme.shadows[4],
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: data.payload.color,
              mb: 0.5,
            }}
          >
            {data.payload.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Value: {formatCash(data.value)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Percentage: {percent}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Empty state
  if (!data || data.length === 0 || total === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
          color: theme.palette.text.secondary,
        }}
      >
        <Typography variant="body2">No data available</Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          dataKey="value"
          label={renderCustomLabel}
          labelLine={false}
          isAnimationActive={true}
          animationBegin={0}
          animationDuration={800}
          animationEasing="ease-out"
          paddingAngle={2}
          onMouseEnter={onPieEnter}
          onMouseLeave={onPieLeave}
          style={{
            filter: activeIndex !== null
              ? "drop-shadow(0 4px 8px rgba(0,0,0,0.2))"
              : "none",
            transition: "filter 0.3s ease",
          }}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.color}
              stroke={isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}
              strokeWidth={1}
              style={{
                opacity: activeIndex === null || activeIndex === index ? 1 : 0.6,
                transition: "opacity 0.3s ease, transform 0.3s ease",
                transform:
                  activeIndex === index ? "scale(1.05)" : "scale(1)",
                transformOrigin: "center",
              }}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{
            paddingTop: "20px",
          }}
          iconType="circle"
          formatter={(value, entry: any) => {
            const item = data.find((d) => d.name === value);
            return (
              <span
                style={{
                  color: isDark
                    ? theme.palette.text.primary
                    : theme.palette.text.primary,
                  fontSize: isMobile ? "0.75rem" : "0.875rem",
                }}
              >
                {value}: {item ? formatCash(item.value) : ""}
              </span>
            );
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default DonutChart;

